'use client';

// Device-scoped "first proposal created" flag (spec 113-proposal-flow-redesign,
// FR-016/FR-018). The first time a creator successfully creates a market we show
// a one-time "what happens next" onboarding panel before the share screen; on
// every subsequent creation we go straight to the share screen. Detection is a
// device-local localStorage flag (clarification 2026-06-10) — a brand-new device
// may re-show the panel once, which is acceptable.

import type { StoreApi, UseBoundStore } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import { safeLocalStorage } from '@/lib/browser/safe-local-storage';
import { createTrackedStore } from '@/lib/dev/zustand-registry';

interface ProposalFirstCreationState {
  /** True once the one-time onboarding panel has been shown. */
  seen: boolean;
  /** Idempotently record that the onboarding panel has been shown. */
  markSeen: () => void;
}

export const useProposalFirstCreationStore: UseBoundStore<StoreApi<ProposalFirstCreationState>> =
  createTrackedStore<ProposalFirstCreationState>('useProposalFirstCreationStore')(
    persist(
      (set, get) => ({
        seen: false,
        markSeen: (): void => {
          if (get().seen) return;
          set({ seen: true });
        },
      }),
      {
        name: 'morfi-proposal-first-creation-seen',
        partialize: (
          state: ProposalFirstCreationState,
        ): Pick<ProposalFirstCreationState, 'seen'> => ({ seen: state.seen }),
        storage: createJSONStorage(() => safeLocalStorage),
      },
    ),
  );
